import React, { useState, useEffect, useCallback } from "react";
import client, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { useApp } from "@/context/AppContext";

export function useFetch(url, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const refetch = useCallback(() => {
    if (!url) return;
    setLoading(true);
    client.get(url).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [url]);
  useEffect(() => { refetch(); /* eslint-disable-next-line */ }, deps);
  return { data, loading, refetch, setData };
}

export function Loading() {
  return <div className="flex items-center justify-center h-64 text-muted-foreground gap-2"><Loader2 className="animate-spin" size={20} /> Loading...</div>;
}

export function PageHeader({ title, subtitle, children, testid }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-testid={testid}>
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight font-heading">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );
}

const STATUS_STYLES = {
  paid: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  unpaid: "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30",
  partial: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
  overdue: "bg-red-600/20 text-red-700 dark:text-red-300 border-red-600/40",
  active: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  inactive: "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30",
  present: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  absent: "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30",
  excused: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
  late: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
  complete: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  missing: "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30",
  expired: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
};

export function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  return (
    <span data-testid={`status-${s}`} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border capitalize ${STATUS_STYLES[s] || "bg-secondary text-secondary-foreground border-border"}`}>
      {status || "—"}
    </span>
  );
}

export function ConfirmDialog({ open, onOpenChange, title, description, onConfirm, confirmText = "Delete", danger = true }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="confirm-ok" className={danger ? "bg-rose-600 hover:bg-rose-700" : ""} onClick={onConfirm}>{confirmText}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function StatCard({ label, value, icon: Icon, tone = "emerald", sub, index = 0, testid }) {
  const tones = {
    emerald: "text-emerald-600 bg-emerald-500/10",
    blue: "text-blue-600 bg-blue-500/10",
    amber: "text-amber-600 bg-amber-500/10",
    rose: "text-rose-600 bg-rose-500/10",
    slate: "text-slate-600 bg-slate-500/10",
    violet: "text-violet-600 bg-violet-500/10",
  };
  return (
    <Card data-testid={testid} className="p-5 stat-card-enter hover:shadow-md transition-shadow" style={{ animationDelay: `${index * 40}ms` }}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="text-2xl font-extrabold mt-2 font-heading">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        {Icon && <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tones[tone]}`}><Icon size={20} /></div>}
      </div>
    </Card>
  );
}

// Reusable field renderer for GenericCrud forms
function Field({ f, value, onChange }) {
  const common = { id: f.key, "data-testid": `field-${f.key}`, value: value ?? "", onChange: (e) => onChange(f.key, e.target.value) };
  if (f.type === "textarea") return <Textarea {...common} rows={3} />;
  if (f.type === "select")
    return (
      <Select value={value ? String(value) : ""} onValueChange={(v) => onChange(f.key, v)}>
        <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder={f.placeholder || "Select"} /></SelectTrigger>
        <SelectContent>{(f.options || []).map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    );
  return <Input type={f.type || "text"} {...common} />;
}

export function EntityForm({ open, onOpenChange, title, fields, initial, onSubmit }) {
  const [form, setForm] = useState(initial || {});
  useEffect(() => { setForm(initial || {}); }, [initial, open]);
  const change = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    try { await onSubmit(form); onOpenChange(false); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          {fields.map((f) => (
            <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
              <Label className="text-xs mb-1.5 block">{f.label}</Label>
              <Field f={f} value={form[f.key]} onChange={change} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel">Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submit} data-testid="form-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function GenericCrud({ title, subtitle, endpoint, columns, fields, canEdit = true, testid, extraHeader }) {
  const { data, loading, refetch } = useFetch(endpoint, [endpoint]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [del, setDel] = useState(null);
  const { t } = useApp();

  const submit = async (form) => {
    if (editing) await client.put(`${endpoint}/${editing.id}`, form);
    else await client.post(endpoint, form);
    toast.success("Saved");
    refetch();
  };
  const doDelete = async () => {
    try { await client.delete(`${endpoint}/${del.id}`); toast.success("Archived"); setDel(null); refetch(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid={testid}>
      <PageHeader title={title} subtitle={subtitle}>
        {extraHeader}
        {canEdit && <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setEditing(null); setOpen(true); }} data-testid={`${testid}-add`}><Plus size={16} className="me-1" /> {t("add")}</Button>}
      </PageHeader>
      {loading ? <Loading /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>{columns.map((c) => <th key={c.key} className="text-start font-semibold px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{c.label}</th>)}{canEdit && <th className="px-4 py-3"></th>}</tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(data || []).length === 0 && <tr><td colSpan={columns.length + 1} className="text-center py-12 text-muted-foreground">{t("no_data")}</td></tr>}
                {(data || []).map((row) => (
                  <tr key={row.id} className="hover:bg-muted/40 transition-colors" data-testid={`${testid}-row-${row.id}`}>
                    {columns.map((c) => <td key={c.key} className="px-4 py-3">{c.render ? c.render(row) : (row[c.key] ?? "—")}</td>)}
                    {canEdit && (
                      <td className="px-4 py-3 text-end whitespace-nowrap">
                        <Button size="icon" variant="ghost" onClick={() => { setEditing(row); setOpen(true); }} data-testid={`${testid}-edit-${row.id}`}><Pencil size={15} /></Button>
                        <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => setDel(row)} data-testid={`${testid}-delete-${row.id}`}><Trash2 size={15} /></Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <EntityForm open={open} onOpenChange={setOpen} title={editing ? `${t("edit")} ${title}` : `${t("add")} ${title}`} fields={fields} initial={editing} onSubmit={submit} />
      <ConfirmDialog open={!!del} onOpenChange={(v) => !v && setDel(null)} title="Archive record?" description="This will move the record to Trash. You can restore it later." confirmText="Archive" onConfirm={doDelete} />
    </div>
  );
}

export function fmtMAD(n) {
  return `${(Number(n) || 0).toLocaleString()} MAD`;
}

export function fileSrc(url) {
  if (!url) return "";
  const token = localStorage.getItem("token");
  const full = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return `${full}${full.includes("?") ? "&" : "?"}auth=${token}`;
}

export async function downloadFile(path, filename) {
  try {
    const r = await client.get(path, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (e) {
    toast.error("Download failed");
  }
}

export function FileUpload({ onUploaded, accept, label = "Upload", testid, size = "sm", variant = "outline" }) {
  const [busy, setBusy] = useState(false);
  const ref = React.useRef();
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await client.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(r.data);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <>
      <input type="file" ref={ref} accept={accept} className="hidden" onChange={handle} data-testid={testid} />
      <Button type="button" variant={variant} size={size} disabled={busy} onClick={() => ref.current?.click()}>
        {busy ? <Loader2 className="animate-spin" size={14} /> : label}
      </Button>
    </>
  );
}
